<template>
  <div class="delete-page">
    <!-- Header -->
    <header class="page-header">
      <div class="header-inner">
        <p class="brand">Hayok Insurance</p>
        <h1>Delete <em>Account</em></h1>
        <p class="meta">This action is permanent and cannot be undone</p>
      </div>
    </header>

    <!-- Confirm flow -->
    <template v-if="step === 'confirm'">
      <div class="layout">
        <!-- Sidebar TOC -->
        <aside class="sidebar">
          <p class="toc-label">On this page</p>
          <a
            v-for="link in sidebarLinks"
            :key="link.id"
            :href="`#${link.id}`"
            class="toc-link"
            :class="{ active: activeSection === link.id }"
            >{{ link.label }}</a
          >
        </aside>

        <!-- Main -->
        <main class="content">
          <div id="overview" class="intro-box">
            <span class="warn-icon">⚠</span>
            <div>
              <strong>You are about to permanently delete your account.</strong>
              Please review everything that will be lost before making your
              decision.
            </div>
          </div>

          <div
            v-for="(item, index) in consequences"
            :key="item.id"
            :id="item.id"
            class="section-card"
          >
            <div class="section-header">
              <span class="section-number">{{
                String(index + 1).padStart(2, "0")
              }}</span>
              <h2 class="section-title">{{ item.title }}</h2>
            </div>
            <p class="section-body">{{ item.description }}</p>
          </div>

          <div id="decision" class="section-card decision-card">
            <div class="section-header">
              <span class="section-number danger-num">!</span>
              <h2 class="section-title">Your Decision</h2>
            </div>
            <p class="section-body" style="margin-bottom: 1.5rem">
              Once deleted, your account and all associated data cannot be
              recovered. If you wish to use Hayok Insurance again in the future,
              you will need to register a new account.
            </p>

            <label class="check-label">
              <input type="checkbox" v-model="understood" class="check-input" />
              <span class="check-box" :class="{ checked: understood }">
                <svg v-if="understood" viewBox="0 0 12 10" fill="none">
                  <path
                    d="M1 5l3.5 3.5L11 1"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="check-text">
                I understand that deleting my account is permanent and all my
                data will be lost.
              </span>
            </label>

            <div class="action-row">
              <button class="btn-keep" @click="handleCancel">
                ← Keep My Account
              </button>
              <button
                class="btn-delete"
                :class="{ enabled: understood }"
                :disabled="!understood"
                @click="handleDelete"
              >
                Permanently Delete
              </button>
            </div>
          </div>
        </main>
      </div>
    </template>

    <!-- Deleting -->
    <template v-else-if="step === 'deleting'">
      <div class="status-wrap">
        <div class="spinner" />
        <h2 class="status-title">Deleting your account…</h2>
        <p class="status-body">
          Please wait while we securely remove your data.
        </p>
      </div>
    </template>

    <!-- Done -->
    <template v-else-if="step === 'done'">
      <div class="status-wrap">
        <div class="done-circle">
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12l4.5 4.5L19 7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <h2 class="status-title">Account Deleted</h2>
        <p class="status-body">
          Your account and all associated data have been permanently removed.
          We're sorry to see you go.
        </p>
        <a href="/" class="btn-home">Return to Homepage</a>
      </div>
    </template>

    <footer class="page-footer">
      &copy; Hayok Insurance &nbsp;·&nbsp; hayokinsurance.com &nbsp;·&nbsp; Need
      help? <a href="mailto:contact@hayokinsurance.com">Contact us</a>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

const step = ref("confirm");
const understood = ref(false);
const activeSection = ref("");

const consequences = [
  {
    id: "policies",
    title: "Policies & Coverage History",
    description:
      "All your insurance policies, coverage details, and renewal history will be permanently erased and cannot be recovered.",
  },
  {
    id: "claims",
    title: "Claims & Documents",
    description:
      "Any submitted or pending claims, uploaded documents, and proof of insurance records will be deleted from our systems immediately.",
  },
  {
    id: "billing",
    title: "Billing & Payment History",
    description:
      "Your payment history, saved payment methods, and invoices will be permanently removed. Download any records you need beforehand.",
  },
  {
    id: "active",
    title: "Active Policies Cancelled",
    description:
      "Any currently active insurance policies linked to this account will be cancelled. Ensure you have alternative coverage in place before proceeding.",
  },
  {
    id: "personal",
    title: "Personal Information",
    description:
      "All personally identifiable information — name, address, contact details — will be wiped in accordance with our data retention policy.",
  },
  {
    id: "access",
    title: "Immediate Access Revocation",
    description:
      "You will be logged out and lose access to your dashboard, support history, and saved preferences instantly upon deletion.",
  },
];

const sidebarLinks = [
  { id: "overview", label: "Overview" },
  ...consequences.map((c) => ({ id: c.id, label: c.title })),
  { id: "decision", label: "Your Decision" },
];

let observer;

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) =>
      entries.forEach((e) => {
        if (e.isIntersecting) activeSection.value = e.target.id;
      }),
    { rootMargin: "-25% 0px -65% 0px" }
  );
  sidebarLinks.forEach(({ id }) => {
    const el = document.getElementById(id);
    if (el) observer.observe(el);
  });
});

onUnmounted(() => observer?.disconnect());

const handleCancel = () => window.history.back();

const handleDelete = async () => {
  if (!understood.value) return;
  step.value = "deleting";
  await new Promise((r) => setTimeout(r, 2200));
  step.value = "done";
};
</script>

<style scoped>
.delete-page {
  min-height: 100vh;
  background: #f5f2ec;
  color: #202948;
  font-family: "DM Sans", sans-serif;
  font-weight: 300;
  display: flex;
  flex-direction: column;
}

/* Header */
.page-header {
  background: #202948;
  color: #f5f2ec;
  padding: 2.5rem 2rem 2.2rem;
  position: relative;
  overflow: hidden;
}
.page-header::before {
  content: "";
  position: absolute;
  right: -80px;
  top: -80px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  border: 60px solid #ffb562;
  opacity: 0.15;
  pointer-events: none;
}
.header-inner {
  max-width: 860px;
  margin: 0 auto;
  position: relative;
}
.brand {
  font-size: 0.72rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #ffb562;
  margin-bottom: 0.9rem;
  font-weight: 500;
}
.header-inner h1 {
  font-family: "DM Serif Display", serif;
  font-size: clamp(2rem, 5vw, 3.4rem);
  font-weight: 400;
  line-height: 1.1;
}
.header-inner h1 em {
  color: #ffb562;
  font-style: italic;
}
.meta {
  margin-top: 1.2rem;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  color: rgba(245, 242, 236, 0.4);
  text-transform: uppercase;
}

/* Layout */
.layout {
  display: grid;
  grid-template-columns: 220px 1fr;
  max-width: 1060px;
  margin: 0 auto;
  padding: 0 1.5rem 5rem;
  align-items: start;
  flex: 1;
  width: 100%;
}
@media (max-width: 700px) {
  .layout {
    grid-template-columns: 1fr;
  }
  .sidebar {
    display: none;
  }
}

/* Sidebar */
.sidebar {
  position: sticky;
  top: 2rem;
  padding: 2.5rem 1.5rem 2.5rem 0;
}
.toc-label {
  font-size: 0.68rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #7a6f65;
  margin-bottom: 1rem;
}
.toc-link {
  display: block;
  font-size: 0.82rem;
  color: #7a6f65;
  text-decoration: none;
  padding: 0.4rem 0 0.4rem 0.9rem;
  border-left: 2px solid #d9d4cb;
  transition: color 0.2s, border-color 0.2s;
  line-height: 1.35;
}
.toc-link:hover,
.toc-link.active {
  color: #ffb562;
  border-color: #ffb562;
}

/* Content */
.content {
  padding: 3rem 0 0 2.5rem;
  border-left: 1px solid #d9d4cb;
}
@media (max-width: 700px) {
  .content {
    padding: 2rem 0 0;
    border-left: none;
  }
}

/* Intro box */
.intro-box {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: #202948;
  color: #f5f2ec;
  border-radius: 4px;
  padding: 1.6rem 2rem;
  margin-bottom: 1.4rem;
  font-size: 0.9rem;
  line-height: 1.75;
  scroll-margin-top: 5rem;
}
.intro-box strong {
  display: block;
  color: #ffb562;
  margin-bottom: 0.3rem;
  font-weight: 500;
}
.warn-icon {
  font-size: 1.3rem;
  flex-shrink: 0;
  color: #ffb562;
  margin-top: 0.15rem;
}

/* Section cards */
.section-card {
  background: #fff;
  border: 1px solid #d9d4cb;
  border-radius: 4px;
  padding: 2rem;
  margin-bottom: 1.2rem;
  scroll-margin-top: 5rem;
  transition: box-shadow 0.25s;
}
.section-card:hover {
  box-shadow: 0 4px 24px rgba(32, 41, 72, 0.07);
}
.section-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}
.section-number {
  flex-shrink: 0;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #ffb562;
  background: rgba(255, 181, 98, 0.12);
  border: 1px solid rgba(255, 181, 98, 0.35);
  border-radius: 2px;
  padding: 0.25rem 0.55rem;
  margin-top: 0.2rem;
}
.danger-num {
  color: #dc3535;
  background: rgba(220, 53, 53, 0.08);
  border-color: rgba(220, 53, 53, 0.25);
}
.section-title {
  font-family: "DM Serif Display", serif;
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 1.3;
}
.section-body {
  font-size: 0.9rem;
  line-height: 1.8;
  color: #2e2a26;
}

/* Decision card */
.decision-card {
  border-color: rgba(220, 53, 53, 0.2);
}

.check-label {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  cursor: pointer;
  margin-bottom: 2rem;
}
.check-input {
  display: none;
}
.check-box {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 2px solid #d9d4cb;
  border-radius: 3px;
  margin-top: 0.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, border-color 0.2s;
  color: #202948;
}
.check-box.checked {
  background: #ffb562;
  border-color: #ffb562;
}
.check-box svg {
  width: 12px;
  height: 10px;
}
.check-text {
  font-size: 0.88rem;
  line-height: 1.65;
  color: #2e2a26;
}

.action-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.btn-keep {
  flex: 1;
  min-width: 160px;
  padding: 0.85rem 1.5rem;
  background: transparent;
  color: #202948;
  border: 1.5px solid #d9d4cb;
  border-radius: 4px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.btn-keep:hover {
  background: #f0ede7;
  border-color: #b8b3aa;
}

.btn-delete {
  flex: 1;
  min-width: 160px;
  padding: 0.85rem 1.5rem;
  background: #d9d4cb;
  color: #a09890;
  border: 1.5px solid transparent;
  border-radius: 4px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  cursor: not-allowed;
  transition: background 0.2s, color 0.2s;
}
.btn-delete.enabled {
  background: #dc3535;
  color: #fff;
  cursor: pointer;
}
.btn-delete.enabled:hover {
  background: #b92b2b;
}

/* Status screens */
.status-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 5rem 2rem;
  gap: 1.2rem;
}
.spinner {
  width: 48px;
  height: 48px;
  border: 3px solid #d9d4cb;
  border-top-color: #ffb562;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.done-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255, 181, 98, 0.12);
  border: 2px solid rgba(255, 181, 98, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffb562;
}
.done-circle svg {
  width: 28px;
  height: 28px;
}
.status-title {
  font-family: "DM Serif Display", serif;
  font-size: 1.8rem;
  font-weight: 400;
  color: #202948;
}
.status-body {
  font-size: 0.9rem;
  line-height: 1.75;
  color: #7a6f65;
  max-width: 420px;
}
.btn-home {
  margin-top: 0.5rem;
  display: inline-block;
  padding: 0.85rem 2rem;
  background: #202948;
  color: #f5f2ec;
  text-decoration: none;
  border-radius: 4px;
  font-size: 0.88rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  transition: background 0.2s;
}
.btn-home:hover {
  background: #2e3d66;
}

/* Footer */
.page-footer {
  background: #202948;
  color: rgba(245, 242, 236, 0.45);
  text-align: center;
  padding: 2rem;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
}
.page-footer a {
  color: #ffb562;
  text-decoration: none;
}
.page-footer a:hover {
  text-decoration: underline;
}
</style>
